import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authApi } from "@/services/api";
import { useCompanySettings, useSiteSettings, resolveUploadUrl } from "@/hooks/useCompanySettings";

export default function LoginPage() {
  const navigate = useNavigate();
  const { settings: mockCompanySettings } = useCompanySettings();
  const siteSettings = useSiteSettings();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rawError, setRawError] = useState("");

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setRawError("");
    setLoading(true);

    try {
      const data = await authApi.login(email, password);
      localStorage.setItem("token", data.token);
      localStorage.setItem("erp_user", JSON.stringify(data.user));
      if (rememberMe) localStorage.setItem("erp_remember", "true");

      if (data.user.role === "Client Portal User") {
        navigate("/portal");
      } else {
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Login failed";
      const isNetworkError =
        message.includes("Unable to connect") ||
        message.includes("fetch") ||
        message.includes("NetworkError") ||
        message.includes("Failed to fetch") ||
        message.includes("ERR_CONNECTION") ||
        message.includes("Load failed");

      if (isNetworkError) {
        setError("Cannot reach the server. Please check your connection and try again.");
        setRawError(message);
      } else if (message.includes("inactive") || message.includes("suspended")) {
        setError("Your account is inactive or suspended. Contact your administrator.");
      } else if (message.includes("Invalid email or password") || message.includes("Invalid credentials")) {
        setError("Invalid email or password. Please try again.");
      } else if (message.includes("Too many login") || message.includes("Too many requests")) {
        setError("Too many login attempts. Please wait 15 minutes and try again.");
      } else if (message.includes("valid email") || message.includes("at least 6") || message.includes("400")) {
        setError("Please enter a valid email address and password (minimum 6 characters).");
      } else {
        setError(message || "Login failed. Please try again.");
        setRawError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const companyName = siteSettings.site_title || mockCompanySettings.name;
  const logoUrl = resolveUploadUrl(siteSettings.site_logo_url || mockCompanySettings.logo);

  return (
    <div className="min-h-screen flex overflow-hidden" style={{ background: "#1c1008" }}>

      {/* ── Left decorative panel ── */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] relative overflow-hidden p-12"
        style={{ background: "linear-gradient(145deg, #1c1008 0%, #2d1a0a 50%, #3d2410 100%)" }}
      >
        {/* Amber glow blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-80px] left-[-80px] w-[420px] h-[420px] rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, #f59e0b 0%, transparent 70%)" }} />
          <div className="absolute bottom-[-60px] right-[-60px] w-[320px] h-[320px] rounded-full opacity-15"
            style={{ background: "radial-gradient(circle, #d97706 0%, transparent 70%)" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-5"
            style={{ background: "radial-gradient(circle, #fbbf24 0%, transparent 60%)" }} />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(rgba(251,191,36,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.4) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Brand */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(251,191,36,0.15)" }}>
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="w-7 h-7 object-contain" />
            ) : (
              <i className="ri-survey-line text-xl" style={{ color: "#fbbf24" }} />
            )}
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight">{companyName}</p>
            <p className="text-xs" style={{ color: "rgba(251,191,36,0.5)" }}>Enterprise ERP</p>
          </div>
        </div>

        {/* Hero text */}
        <div className="relative z-10 space-y-6">
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight">
              Manage your<br />
              <span style={{ color: "#fbbf24" }}>survey projects</span><br />
              with precision.
            </h2>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>
              From job cards to deliverables, billing to field tracking — everything your survey team needs in one unified platform.
            </p>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2">
            {["Job Card Management", "GPS Field Tracking", "Client Portal", "Billing & Invoicing", "Team Management"].map((f) => (
              <span key={f} className="text-xs px-3 py-1.5 rounded-full font-medium"
                style={{ background: "rgba(251,191,36,0.1)", color: "rgba(251,191,36,0.8)", border: "1px solid rgba(251,191,36,0.15)" }}
              >
                {f}
              </span>
            ))}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 pt-2">
            {[
              { value: "500+", label: "Job Cards" },
              { value: "99.9%", label: "Uptime" },
              { value: "24/7", label: "Support" },
            ].map((s) => (
              <div key={s.label} className="text-center p-3 rounded-xl" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.1)" }}>
                <p className="text-xl font-bold" style={{ color: "#fbbf24" }}>{s.value}</p>
                <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>
            &copy; {new Date().getFullYear()} {companyName}. Powered by{" "}
            <a href="https://digicube.net.in" target="_blank" rel="noopener noreferrer"
              className="underline underline-offset-2 transition-colors"
              style={{ color: "rgba(251,191,36,0.5)" }}
            >
              Digicube Solutions
            </a>
          </p>
        </div>
      </div>

      {/* ── Right login panel ── */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative" style={{ background: "#fafaf8" }}>

        {/* Mobile logo */}
        <div className="absolute top-6 left-6 flex items-center gap-2 lg:hidden">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "#fffbeb" }}>
            {logoUrl
              ? <img src={logoUrl} alt={companyName} className="w-5 h-5 object-contain" />
              : <i className="ri-survey-line text-base" style={{ color: "#d97706" }} />
            }
          </div>
          <p className="text-sm font-bold text-gray-800">{companyName}</p>
        </div>

        <div className="w-full max-w-sm">
          {/* Heading */}
          <div className="mb-8">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: "#fffbeb" }}>
              <i className="ri-login-circle-line text-2xl" style={{ color: "#d97706" }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-sm text-gray-500 mt-1">Sign in to your ERP dashboard</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 p-3.5 rounded-xl bg-red-50 border border-red-200 flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-500 text-base" />
                </span>
                <span className="text-sm text-red-600">{error}</span>
              </div>
              {rawError && rawError !== error && (
                <p className="text-xs text-red-400 pl-6 font-mono break-all">Server: {rawError}</p>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-400 text-base" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                  style={{ "--tw-ring-color": "rgba(245,158,11,0.25)" } as React.CSSProperties}
                  onFocus={(e) => { e.target.style.borderColor = "#f59e0b"; e.target.style.boxShadow = "0 0 0 3px rgba(245,158,11,0.12)"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; }}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-gray-400 text-base" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all"
                  onFocus={(e) => { e.target.style.borderColor = "#f59e0b"; e.target.style.boxShadow = "0 0 0 3px rgba(245,158,11,0.12)"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; }}
                  required
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600"
                >
                  <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-base`} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                  style={{ accentColor: "#d97706" }}
                />
                <span className="text-sm text-gray-600">Remember Me</span>
              </label>
              <button type="button" className="text-sm font-medium cursor-pointer whitespace-nowrap transition-colors hover:underline" style={{ color: "#d97706" }}>
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 mt-2"
              style={{
                background: loading
                  ? "#9ca3af"
                  : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                boxShadow: loading ? "none" : "0 4px 14px rgba(245,158,11,0.35)",
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-base" />
                  Signing in...
                </span>
              ) : (
                "Sign In to Dashboard"
              )}
            </button>
          </form>

          {/* Divider hint */}
          <div className="mt-6 p-4 rounded-xl border" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
            <div className="flex items-start gap-2.5">
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-sm" style={{ color: "#d97706" }} />
              </span>
              <p className="text-xs leading-relaxed" style={{ color: "#92400e" }}>
                Use your company-issued credentials. Contact your system administrator if you&apos;re unable to log in.
              </p>
            </div>
          </div>

          {/* Portal links */}
          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 whitespace-nowrap">Other portals</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Link
              to="/client-portal"
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all group cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 bg-amber-50 group-hover:bg-amber-100 transition-colors">
                <i className="ri-user-star-line text-base" style={{ color: "#d97706" }} />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-700 group-hover:text-amber-700 transition-colors whitespace-nowrap">Client Portal</p>
                <p className="text-xs text-gray-400 truncate">Track your projects</p>
              </div>
            </Link>
            <Link
              to="/field-app"
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all group cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 bg-amber-50 group-hover:bg-amber-100 transition-colors">
                <i className="ri-map-pin-user-line text-base" style={{ color: "#d97706" }} />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-700 group-hover:text-amber-700 transition-colors whitespace-nowrap">Field App</p>
                <p className="text-xs text-gray-400 truncate">Field check-in</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
